import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useLanguage } from "../i18n/LanguageContext";

const CONTENT = {
  fr: {
    title: "Mentions légales",
    back: "Retour à l'accueil",
    sections: [
      {
        h: "Éditeur du site",
        body: (
          <>
            <p><strong>Trois Couronnes Productions</strong> — Société par actions simplifiée (SAS)</p>
            <p>Siège social : 60 rue François Ier, 75008 Paris, France</p>
            <p>SIRET : 100 855 527 00014 — SIREN : 100 855 527</p>
            <p>Directeurs de la publication : Baptiste Lorber & Nabiha Akkari</p>
            <p>
              Contact :{" "}
              <a className="tcp-link" href="mailto:baptiste@troiscouronnesproductions.com">
                baptiste@troiscouronnesproductions.com
              </a>{" "}
              ·{" "}
              <a className="tcp-link" href="mailto:nabiha@troiscouronnesproductions.com">
                nabiha@troiscouronnesproductions.com
              </a>
            </p>
          </>
        ),
      },
      {
        h: "Hébergement",
        body: (
          <p>
            Le site est hébergé par <strong>Emergent</strong> —{" "}
            <a className="tcp-link" href="https://emergent.sh" target="_blank" rel="noopener noreferrer">
              emergent.sh
            </a>
            .
          </p>
        ),
      },
      {
        h: "Propriété intellectuelle",
        body: (
          <p>
            L'ensemble des contenus présents sur ce site (textes, visuels, logos, pitchs, illustrations,
            bandes-annonces) sont la propriété exclusive de Trois Couronnes Productions ou de leurs ayants
            droit. Toute reproduction, représentation, modification, publication ou adaptation, partielle ou
            intégrale, par quelque procédé que ce soit et sur quelque support que ce soit, est interdite sans
            l'autorisation écrite préalable de Trois Couronnes Productions.
          </p>
        ),
      },
      {
        h: "Données personnelles",
        body: (
          <>
            <p>
              Ce site ne collecte aucune donnée personnelle via un formulaire. Les seules données échangées le
              sont à l'initiative de l'utilisateur lorsqu'il contacte les producteurs par e-mail.
            </p>
            <p>
              Conformément au Règlement Général sur la Protection des Données (RGPD) et à la loi Informatique
              et Libertés, vous disposez d'un droit d'accès, de rectification et de suppression des données vous
              concernant. Pour exercer ce droit, écrivez à l'une des adresses indiquées ci-dessus.
            </p>
          </>
        ),
      },
      {
        h: "Cookies",
        body: (
          <p>
            Le site stocke localement une préférence de langue (FR / EN) dans votre navigateur. Aucun cookie de
            traçage publicitaire n'est déposé.
          </p>
        ),
      },
    ],
  },
  en: {
    title: "Legal notice",
    back: "Back to homepage",
    sections: [
      {
        h: "Site publisher",
        body: (
          <>
            <p><strong>Trois Couronnes Productions</strong> — Société par Actions Simplifiée (SAS)</p>
            <p>Registered office: 60 rue François Ier, 75008 Paris, France</p>
            <p>SIRET: 100 855 527 00014 — SIREN: 100 855 527</p>
            <p>Publication directors: Baptiste Lorber & Nabiha Akkari</p>
            <p>
              Contact:{" "}
              <a className="tcp-link" href="mailto:baptiste@troiscouronnesproductions.com">
                baptiste@troiscouronnesproductions.com
              </a>{" "}
              ·{" "}
              <a className="tcp-link" href="mailto:nabiha@troiscouronnesproductions.com">
                nabiha@troiscouronnesproductions.com
              </a>
            </p>
          </>
        ),
      },
      {
        h: "Hosting",
        body: (
          <p>
            This website is hosted by <strong>Emergent</strong> —{" "}
            <a className="tcp-link" href="https://emergent.sh" target="_blank" rel="noopener noreferrer">
              emergent.sh
            </a>
            .
          </p>
        ),
      },
      {
        h: "Intellectual property",
        body: (
          <p>
            All content displayed on this website (texts, visuals, logos, pitches, illustrations, trailers)
            is the exclusive property of Trois Couronnes Productions or its rights holders. Any reproduction,
            representation, modification, publication or adaptation — whether partial or full and by any
            means — is forbidden without the prior written authorisation of Trois Couronnes Productions.
          </p>
        ),
      },
      {
        h: "Personal data",
        body: (
          <>
            <p>
              This website does not collect any personal data through a form. The only data exchanged comes
              from users contacting the producers directly by e-mail.
            </p>
            <p>
              In accordance with the General Data Protection Regulation (GDPR) and the French Data Protection
              Act, you have the right to access, rectify and delete your personal data. To exercise this right,
              please write to one of the e-mail addresses listed above.
            </p>
          </>
        ),
      },
      {
        h: "Cookies",
        body: (
          <p>
            The website stores a language preference (FR / EN) locally in your browser. No advertising
            tracking cookies are set.
          </p>
        ),
      },
    ],
  },
};

export default function MentionsLegales() {
  const { lang, setLang, t } = useLanguage();
  const c = CONTENT[lang];

  return (
    <div className="min-h-screen bg-[#050505] text-[#F5F5F0]" data-testid="mentions-legales-page">
      <header className="fixed top-0 left-0 right-0 z-50 bg-black/70 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-20">
          <Link
            to="/"
            data-testid="ml-logo-link"
            className="flex items-center gap-3 group"
          >
            <img
              src="/logo.png"
              alt="Trois Couronnes Productions"
              className="h-12 w-12 object-contain transition-transform duration-500 group-hover:scale-105"
            />
            <span className="hidden md:block font-display text-[15px] tracking-wide text-[#F5F5F0]">
              Trois Couronnes
              <span className="block text-[10px] tracking-[0.32em] text-[#9c9b94] uppercase mt-0.5">
                Productions
              </span>
            </span>
          </Link>

          <div className="inline-flex items-center border border-white/15" role="group" aria-label={t.language.label}>
            {["fr", "en"].map((code) => (
              <button
                key={code}
                type="button"
                onClick={() => setLang(code)}
                className={`px-3 py-1.5 text-[11px] tracking-[0.28em] uppercase transition-colors duration-300 ${
                  lang === code
                    ? "bg-[#C5A059] text-[#050505]"
                    : "text-[#A3A3A3] hover:text-[#F5F5F0]"
                }`}
              >
                {t.language[code]}
              </button>
            ))}
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 md:px-12 pt-40 pb-32">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="flex items-center gap-4 mb-6">
            <span className="h-px w-10 bg-[#C5A059]" />
            <span className="tcp-eyebrow">{c.title}</span>
          </div>
          <h1
            data-testid="ml-title"
            className="font-display text-[#F5F5F0] text-4xl md:text-5xl leading-[1.05]"
          >
            {c.title}
          </h1>
        </motion.div>

        <div className="mt-16 space-y-14">
          {c.sections.map((s, i) => (
            <motion.section
              key={i}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6, delay: 0.05 * i }}
            >
              <h2 className="font-display text-2xl md:text-3xl text-[#F5F5F0] mb-4">
                {s.h}
              </h2>
              <div className="space-y-3 text-[#C9C9C2] text-[15px] leading-[1.8]">
                {s.body}
              </div>
            </motion.section>
          ))}
        </div>

        <Link
          to="/"
          data-testid="ml-back-link"
          className="mt-20 inline-flex items-center gap-3 text-[#C5A059] hover:text-[#F5F5F0] transition-colors text-[12px] tracking-[0.3em] uppercase"
        >
          <svg width="22" height="8" viewBox="0 0 22 8" fill="none" aria-hidden>
            <path d="M22 4H2M6 1L2 4l4 3" stroke="currentColor" strokeWidth="1" />
          </svg>
          {c.back}
        </Link>
      </main>
    </div>
  );
}
